import moment from 'moment';

import {
  ComplexDateOutputValue,
  TIME_DIRECTIONS,
  TIME_GRANULARITIES,
  TimeDirection,
  TimeGranularity,
} from '@/components/ui/daisy/templates/complex-date-input/types';
import { Nullable } from '@/models/Model';
import { getIsoDateParam, getNumberParam } from '@/utils/filters/searchParams';

export type DateRangeFilterParams = {
  dateRangeAfter?: string;
  dateRangeBefore?: string;
  dateRangeDirection?: TimeDirection;
  dateRangeGranularity?: TimeGranularity;
  dateRangeAmount?: number;
};

export const ALL_DATES = 'ALL';

const CLEARED_DATE_RANGE_PARAMS: DateRangeFilterParams = {
  dateRangeAfter: undefined,
  dateRangeBefore: undefined,
  dateRangeDirection: undefined,
  dateRangeGranularity: undefined,
  dateRangeAmount: undefined,
};

export const ALL_DATES_PARAMS: DateRangeFilterParams = {
  ...CLEARED_DATE_RANGE_PARAMS,
  dateRangeAfter: ALL_DATES,
};

const isTimeDirection = (value: string | null): value is TimeDirection =>
  TIME_DIRECTIONS.includes(value as TimeDirection);

const isTimeGranularity = (value: string | null): value is TimeGranularity =>
  TIME_GRANULARITIES.includes(value as TimeGranularity);

const prefixedKey = (key: keyof DateRangeFilterParams, prefix?: string) =>
  prefix ? `${prefix}${key.charAt(0).toUpperCase()}${key.slice(1)}` : key;

export const prefixDateRangeParams = (
  filters: Partial<DateRangeFilterParams>,
  prefix?: string,
): Record<string, string | number | undefined> =>
  Object.fromEntries(
    Object.entries(filters).map(([key, value]) => [
      prefixedKey(key as keyof DateRangeFilterParams, prefix),
      value,
    ]),
  );

const getDiscreteParams = (
  params: URLSearchParams,
  hasDateRange: boolean,
  prefix?: string,
): Pick<
  DateRangeFilterParams,
  'dateRangeDirection' | 'dateRangeGranularity' | 'dateRangeAmount'
> => {
  const direction = params.get(prefixedKey('dateRangeDirection', prefix));
  const granularity = params.get(prefixedKey('dateRangeGranularity', prefix));
  const amount = getNumberParam(params, prefixedKey('dateRangeAmount', prefix));

  if (
    !hasDateRange ||
    !isTimeDirection(direction) ||
    !isTimeGranularity(granularity) ||
    (direction !== 'this' && !amount)
  ) {
    return {};
  }

  return {
    dateRangeDirection: direction,
    dateRangeGranularity: granularity,
    dateRangeAmount: direction === 'this' ? undefined : amount,
  };
};

export const parseDateRangeParams = (
  params: URLSearchParams,
  prefix?: string,
): DateRangeFilterParams => {
  const dateRangeAfter = getIsoDateParam(
    params,
    prefixedKey('dateRangeAfter', prefix),
  );
  const dateRangeBefore = getIsoDateParam(
    params,
    prefixedKey('dateRangeBefore', prefix),
  );

  return {
    dateRangeAfter,
    dateRangeBefore,
    ...getDiscreteParams(params, !!dateRangeAfter && !!dateRangeBefore, prefix),
  };
};

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

export const DEFAULT_PERIOD_IN_MONTHS = 6;

const LAST_MONTHS_DISCRETE_RANGE: Pick<
  DateRangeFilterParams,
  'dateRangeDirection' | 'dateRangeGranularity' | 'dateRangeAmount'
> = {
  dateRangeDirection: 'past',
  dateRangeGranularity: 'month',
  dateRangeAmount: DEFAULT_PERIOD_IN_MONTHS,
};

export const parseDateRangeParamsWithDefault = (
  params: URLSearchParams,
  prefix?: string,
): DateRangeFilterParams => {
  const dateRange = parseDateRangeParams(params, prefix);

  const hasExplicitDate =
    !!dateRange.dateRangeAfter ||
    !!dateRange.dateRangeBefore ||
    params.get(prefixedKey('dateRangeAfter', prefix)) === ALL_DATES;

  if (hasExplicitDate) return dateRange;

  return {
    ...LAST_MONTHS_DISCRETE_RANGE,
    dateRangeAfter: moment()
      .subtract(DEFAULT_PERIOD_IN_MONTHS, 'month')
      .format(ISO_DATE_FORMAT),
    dateRangeBefore: moment().format(ISO_DATE_FORMAT),
  };
};

export const toComplexDateValue = ({
  dateRangeAfter,
  dateRangeBefore,
  dateRangeDirection,
  dateRangeGranularity,
  dateRangeAmount,
}: DateRangeFilterParams): Nullable<ComplexDateOutputValue> => {
  if (!dateRangeAfter && !dateRangeBefore) return null;
  if (
    dateRangeDirection &&
    dateRangeGranularity &&
    dateRangeAfter &&
    dateRangeBefore
  ) {
    return {
      type: 'discrete',
      direction: dateRangeDirection,
      granularity: dateRangeGranularity,
      amount: dateRangeAmount,
      dateFrom: dateRangeAfter,
      dateTo: dateRangeBefore,
    };
  }
  if (dateRangeAfter && dateRangeAfter === dateRangeBefore) {
    return { type: 'single', date: dateRangeAfter };
  }
  return {
    type: 'range',
    dateFrom: dateRangeAfter ?? '',
    dateTo: dateRangeBefore ?? '',
  };
};

export const fromComplexDateValue = (
  value: Nullable<ComplexDateOutputValue>,
): DateRangeFilterParams => {
  if (!value) return CLEARED_DATE_RANGE_PARAMS;
  if (value.type === 'discrete') {
    return {
      dateRangeAfter: value.dateFrom,
      dateRangeBefore: value.dateTo,
      dateRangeDirection: value.direction,
      dateRangeGranularity: value.granularity,
      dateRangeAmount: value.amount,
    };
  }
  if (value.type === 'single') {
    return {
      ...CLEARED_DATE_RANGE_PARAMS,
      dateRangeAfter: value.date,
      dateRangeBefore: value.date,
    };
  }
  return {
    ...CLEARED_DATE_RANGE_PARAMS,
    dateRangeAfter: value.dateFrom || undefined,
    dateRangeBefore: value.dateTo || undefined,
  };
};
